import React, { useEffect, useRef, useState } from "react";
import NameInfo from "./NameInfo.jsx";
import PageHeader from "./PageHeader.jsx";
import FilterPanel from "./FilterPanel.jsx";

export default function InOrOut({ user }) {
  const MIN_QUEUE_SIZE = 15;
  const [name, setName] = useState(null);
  const [meta, setMeta] = useState(null);
  const [nameQueue, setNameQueue] = useState([]);
  const [isNameLoading, setIsNameLoading] = useState(false);
  const [isMetaLoading, setIsMetaLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [lastBatchWasEmpty, setLastBatchWasEmpty] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [history, setHistory] = useState([]);
  const nameRef = useRef(null);
  const metaRequestId = useRef(0);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (Object.keys(filters).length > 0) {
      params.set("filters", JSON.stringify(filters));
    }
    return params;
  };

  const fetchMetaForName = async (nextName) => {
    if (!nextName) {
      setMeta(null);
      return;
    }
    const requestId = ++metaRequestId.current;
    setIsMetaLoading(true);
    try {
      const mres = await fetch(`/api/name/meta/${nextName}`);
      const mdata = await mres.json();
      if (nameRef.current === nextName && requestId === metaRequestId.current) {
        setMeta(mdata);
      }
    } finally {
      if (requestId === metaRequestId.current) {
        setIsMetaLoading(false);
      }
    }
  };

  const setCurrentName = (nextName, nextMeta = null) => {
    nameRef.current = nextName;
    setName(nextName);
    if (nextMeta) {
      setMeta(nextMeta);
      setIsMetaLoading(false);
    } else if (nextName) {
      setMeta(null);
      fetchMetaForName(nextName);
    } else {
      setMeta(null);
      setIsMetaLoading(false);
    }
    setIsNameLoading(false);
  };

  const fetchNextName = async ({ replaceQueue = false, showLoader = false } = {}) => {
    if (showLoader) setIsNameLoading(true);
    setIsFetchingMore(true);
    const params = buildQueryParams();
    const res = await fetch(`/api/name/in-or-out/${user.username}?${params.toString()}`);
    const data = await res.json();
    const incoming = data.name || null;

    setNameQueue((prevQueue) => {
      const base = replaceQueue ? [] : prevQueue;
      if (!incoming) {
        return base;
      }
      if (base.includes(incoming) || nameRef.current === incoming) {
        return base;
      }
      const updated = [...base, incoming];
      if (!nameRef.current) {
        const [next, ...rest] = updated;
        setCurrentName(next);
        return rest;
      }
      return updated;
    });

    if (!incoming) {
      setLastBatchWasEmpty(true);
      if (!nameRef.current) {
        setCurrentName(null);
      }
    } else {
      setLastBatchWasEmpty(false);
    }
    setIsFetchingMore(false);
  };

  useEffect(() => {
    setHistory([]);
    setNameQueue([]);
    setMeta(null);
    setCurrentName(null);
    setIsMetaLoading(false);
    setLastBatchWasEmpty(false);
    fetchNextName({ replaceQueue: true, showLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (isNameLoading || isFetchingMore) return;
    if (!nameRef.current) return;
    if (lastBatchWasEmpty) return;
    if (nameQueue.length < MIN_QUEUE_SIZE) {
      fetchNextName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameQueue, isNameLoading, isFetchingMore, lastBatchWasEmpty]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!name || isNameLoading) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        vote('reject');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        vote('accept');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [name, isNameLoading]);

  const advanceToNextName = () => {
    let needsRefill = false;
    setNameQueue((prevQueue) => {
      if (prevQueue.length === 0) {
        setCurrentName(null);
        needsRefill = true;
        return prevQueue;
      }
      const [next, ...rest] = prevQueue;
      setCurrentName(next);
      return rest;
    });
    if (needsRefill && !isFetchingMore) {
      fetchNextName({ showLoader: true });
    }
  };

  const vote = async (decision) => {
    if (!name) return;

    setIsNameLoading(true);
    setHistory((prev) => [...prev, { name, decision, meta }]);

    advanceToNextName();
    await fetch(`/api/name/in-or-out/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, name })
    });
  };

  const goBack = async () => {
    if (history.length === 0 || !name) return;

    setIsNameLoading(true);

    const lastEntry = history[history.length - 1];
    setNameQueue((prevQueue) => [name, ...prevQueue]);

    await fetch(`/api/name/in-or-out/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision: 'undo', name: lastEntry.name })
    });

    setHistory((prev) => prev.slice(0, -1));
    setCurrentName(lastEntry.name, lastEntry.meta);
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === null || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
    setHistory([]);
  };

  const clearAllFilters = () => {
    setFilters({});
    setHistory([]);
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  if (!name && (isNameLoading || isFetchingMore)) {
    return (
      <div className="page">
        <div className="loading">Finding your next name...</div>
      </div>
    );
  }

  if (!name) {
    return (
      <div className="page">
        <PageHeader
          title={hasActiveFilters ? 'No More Names Match Your Filters' : 'All Done! 🎉'}
          description={hasActiveFilters
            ? 'Try adjusting your filters to see more names, or check out your profile to see your favorites.'
            : "You've seen all the names! Check out your profile to see your favorites."}
        />
        {hasActiveFilters && (
          <div className="filter-reset">
            <button onClick={clearAllFilters} className="btn-primary">
              🔄 Clear Filters & Continue
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      {isNameLoading && (
        <div className="loading-overlay">Finding your next name...</div>
      )}
      <PageHeader
        title="In or Out"
        description="Swipe through names and build your favorites list. Use ← → arrow keys or click the buttons!"
      />

      <div className="name-display">
        <h1 className="name-title">{name}</h1>
        <div className="vote-buttons">
          <button className="reject-btn" onClick={() => vote('reject')} disabled={isNameLoading}>👎</button>
          <button className="accept-btn" onClick={() => vote('accept')} disabled={isNameLoading}>👍</button>
        </div>
        {history.length > 0 && (
          <div className="back-button-container">
            <button
              className="back-btn"
              onClick={goBack}
              disabled={isNameLoading}
              title={`Go back to ${history[history.length - 1].name}`}
            >
              ↶ Undo & Go Back
            </button>
          </div>
        )}
      </div>

      <FilterPanel
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filters={filters}
        updateFilter={updateFilter}
        clearAllFilters={clearAllFilters}
        user={user}
      />

      {isMetaLoading && (
        <div className="loading">Loading details...</div>
      )}
      {meta && !isMetaLoading && <NameInfo name={name} meta={meta} gender={user.gender} showPctToggle={true} />}
    </div>
  );
}
